import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useColorMode } from "@chakra-ui/react";
import { armExplicitColorModeWrite } from "./colorModeManager";
import {
  STYLE_COOKIE,
  readCookie,
  writeCookie,
  type AppearanceMode,
  type AppearanceStyle,
} from "./cookies";

/**
 * The visitor's appearance: colour mode (light/dark, owned by Chakra) and the
 * style axis (Calm/Expressive, owned here).
 *
 * Spec: specs/active/2026-08-29-website-design-system-adoption (Phase 2, Rev 3).
 *
 * The style lives on <html data-ips-style="expressive"> (absent = Calm), which
 * src/styles/tokens.css keys on. The pre-paint script sets it before the first
 * paint; this provider starts from the same cookie and keeps the attribute in
 * step. A change writes the cookie and the attribute synchronously, so there
 * is no frame where the two disagree.
 *
 * This is the ONE module that may call Chakra's `useColorMode` for the
 * appearance control — components ask `useAppearance()` instead, so the
 * explicit-write flag cannot be bypassed.
 */

export const applyStyleAttribute = (style: AppearanceStyle): void => {
  if (typeof document === "undefined") return;
  if (style === "expressive") {
    document.documentElement.setAttribute("data-ips-style", "expressive");
  } else {
    document.documentElement.removeAttribute("data-ips-style");
  }
};

/** Only the exact value `expressive` selects Expressive (whitelist). */
export const readStoredStyle = (): AppearanceStyle =>
  readCookie(STYLE_COOKIE) === "expressive" ? "expressive" : "calm";

type AppearanceContextValue = {
  mode: AppearanceMode;
  style: AppearanceStyle;
  setMode: (mode: AppearanceMode) => void;
  setStyle: (style: AppearanceStyle) => void;
};

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export const AppearanceProvider = ({ children }: { children: ReactNode }) => {
  const { colorMode, setColorMode } = useColorMode();
  const [style, setStyleState] = useState<AppearanceStyle>(readStoredStyle);

  // Idempotent with the pre-paint script; covers the case where it never ran.
  useLayoutEffect(() => {
    applyStyleAttribute(style);
  }, [style]);

  const setStyle = useCallback((next: AppearanceStyle) => {
    writeCookie(STYLE_COOKIE, next);
    applyStyleAttribute(next);
    setStyleState(next);
  }, []);

  const setMode = useCallback(
    (next: AppearanceMode) => {
      armExplicitColorModeWrite();
      setColorMode(next);
    },
    [setColorMode]
  );

  const value = useMemo<AppearanceContextValue>(
    () => ({
      mode: colorMode === "dark" ? "dark" : "light",
      style,
      setMode,
      setStyle,
    }),
    [colorMode, style, setMode, setStyle]
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export const useAppearance = (): AppearanceContextValue => {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error("useAppearance must be used inside <AppearanceProvider>");
  return ctx;
};
